import { lazy } from 'react'

import {
  OneOnOneMeetingHeaderDocument,
  OneOnOnePageHeaderDocument,
} from 'generated/resources/graphql'

import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { EmployeeMeetingHeader } from 'modules/one_on_ones/components/EmployeeMeetingHeader'

const OneOnOnesListPage = lazy(() => import('modules/one_on_ones/pages/OneOnOnesListPage'))
const RootPage = lazy(() => import('modules/one_on_ones/pages/RootPage'))

const OneOnOneDetailPage = lazy(() => import('modules/one_on_ones/pages/OneOnOneDetailPage'))

const MeetingDetailPage = lazy(() => import('modules/one_on_ones/pages/MeetingDetailPage'))

export const performanceOneOnOnesRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.performanceOneOnOnes.path.replace(
        `${navigation.performanceProcess.path}/`,
        ''
      ),
      element: <RootPage />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('one_on_ones.title'),
            description: i18n.t('one_on_ones.description'),
          }
        },
      },

      children: [
        {
          index: true,
          Component: OneOnOnesListPage,
        },
        {
          path: ':oneOnOneId/meetings',
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              if (!params.oneOnOneId) return { title: '' }

              return watchQuery({
                query: OneOnOnePageHeaderDocument,
                variables: {
                  id: String(params.oneOnOneId),
                },
              }).map(({ data }) => {
                const oneOnOne = data?.oneOnOnes.oneOnOne

                if (!oneOnOne) {
                  return {
                    title: '',
                  }
                }

                const firstFullName =
                  oneOnOne.firstParticipant?.fullName ||
                  i18n.t('one_on_ones.meeting.unknown_employee')

                const secondFullName =
                  oneOnOne.secondParticipant?.fullName ||
                  i18n.t('one_on_ones.meeting.unknown_employee')

                return {
                  title: `${firstFullName} / ${secondFullName}`,
                  headerComponent: null, // There is no resource header design yet that shows multiple participants. Thus it is pushed to the page code
                }
              })
            },
          },
          children: [
            {
              Component: OneOnOneDetailPage,
              index: true,
            },
            {
              path: ':meetingId',
              Component: MeetingDetailPage,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  if (!params.meetingId) return { title: '' }

                  return watchQuery({
                    query: OneOnOneMeetingHeaderDocument,
                    variables: {
                      id: String(params.meetingId),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const meeting = data?.oneOnOnes.meeting

                    if (!meeting) return { title: '' }

                    const canGenerateSummary =
                      data?.features.featuresConnection.nodes.find(
                        (feature) => feature.name === 'dev_augment_meeting_with_ai'
                      )?.enabled ?? false

                    return {
                      title: dateBackendDateTimeToUI(meeting.startTime),
                      headerComponent: (
                        <EmployeeMeetingHeader
                          meeting={meeting}
                          canGenerateSummary={canGenerateSummary}
                        />
                      ),
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
}
